import { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import { Account } from '../../services/types';
import { cn } from '../../lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField } from '../../components/forms/formValidationUtils';
import { EntityTypeaheadPicker, type PickerOption } from '../../components/search/EntityTypeaheadPicker';

const lineSchema = z.object({
    account_id: z.string().min(1, 'Account required'),
    debit: z.number().min(0),
    credit: z.number().min(0),
});

const schema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date'),
    description: z.string().min(1, 'Description required'),
    lines: z.array(lineSchema).min(2, 'At least 2 lines required')
}).refine((data) => {
    const totalDebit = data.lines.reduce((sum, line) => sum + (line.debit || 0), 0);
    const totalCredit = data.lines.reduce((sum, line) => sum + (line.credit || 0), 0);
    return Math.abs(totalDebit - totalCredit) < 0.01;
}, {
    message: "Total Debits must equal Total Credits",
    path: ["lines"],
});

type FormData = z.infer<typeof schema>;

export function JournalEntryForm() {
    const [accounts, setAccounts] = useState<Account[]>([]);
    const { register, control, handleSubmit, watch, formState: { errors, isSubmitting }, reset } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            date: new Date().toISOString().split('T')[0],
            lines: [
                { account_id: '', debit: 0, credit: 0 },
                { account_id: '', debit: 0, credit: 0 }
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "lines"
    });

    const lines = watch('lines');
    const totalDebit = lines.reduce((sum, line) => sum + (line.debit || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + (line.credit || 0), 0);
    const accountPickerOptions = useMemo<PickerOption<string>[]>(() => {
        return [...accounts]
            .filter((account) => account.is_active !== false && account.is_postable !== false)
            .sort((left, right) => {
                const leftCode = String(left.code ?? '');
                const rightCode = String(right.code ?? '');
                const leftNumeric = Number(leftCode);
                const rightNumeric = Number(rightCode);
                const bothNumeric = Number.isFinite(leftNumeric) && Number.isFinite(rightNumeric);
                if (bothNumeric && leftNumeric !== rightNumeric) {
                    return leftNumeric - rightNumeric;
                }
                if (leftCode !== rightCode) {
                    return leftCode.localeCompare(rightCode);
                }
                return String(left.name ?? '').localeCompare(String(right.name ?? ''));
            })
            .map((account) => ({
                value: String(account.id),
                label: `${account.code} - ${account.name}`,
                description: account.type ? `${account.type} account` : undefined,
            }));
    }, [accounts]);

    useEffect(() => {
        api.financials.listAccounts().then(setAccounts);
    }, []);

    const onSubmit = async (data: FormData) => {
        try {
            await api.financials.createJournal({
                date: data.date,
                description: data.description,
                lines: data.lines.map(l => ({
                    account_id: parseInt(l.account_id),
                    debit: l.debit,
                    credit: l.credit
                }))
            });
            toast.success("Journal Entry Posted!");
            reset();
        } catch (error) {
            console.error("Failed to post journal", error);
            toast.error("Failed to post journal entry");
        }
    };
    const onInvalid = (formErrors: Record<string, any>) => {
        showValidationSummaryToast();
        focusFirstInvalidField(Object.keys(formErrors)[0]);
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>New Journal Entry</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="date">Date</RequiredLabel>
                            <input
                                id="date"
                                type="date"
                                {...register('date')}
                                aria-invalid={!!errors.date}
                                aria-required="true"
                                aria-describedby={errors.date ? 'date_error' : undefined}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-red-100"
                            />
                            <FieldErrorText id="date_error" message={errors.date?.message} />
                        </div>
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="description">Description</RequiredLabel>
                            <input
                                id="description"
                                type="text"
                                {...register('description')}
                                aria-invalid={!!errors.description}
                                aria-required="true"
                                aria-describedby={errors.description ? 'description_error' : undefined}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-red-100"
                                placeholder="e.g., Monthly Rent"
                            />
                            <FieldErrorText id="description_error" message={errors.description?.message} />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium">Journal Lines</h3>
                            <Button
                                type="button"
                                onClick={() => append({ account_id: '', debit: 0, credit: 0 })}
                                size="sm"
                                variant="outline"
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add Line
                            </Button>
                        </div>

                        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                            Enter amounts with <strong>Debit on the left</strong> and <strong>Credit on the right</strong>.
                        </div>

                        <div className="hidden items-center gap-2 px-1 text-xs font-medium text-muted-foreground md:flex">
                            <div className="flex-1">Account</div>
                            <div className="w-32 text-center">Debit (Left)</div>
                            <div className="w-32 text-center">Credit (Right)</div>
                            <div className="w-10" />
                        </div>

                        <div className="space-y-2">
                            {fields.map((field, index) => (
                                <div key={field.id} className="flex gap-2 items-start">
                                    <div className="flex-1 space-y-1">
                                        <Controller
                                            control={control}
                                            name={`lines.${index}.account_id`}
                                            render={({ field: accountField }) => (
                                                <EntityTypeaheadPicker
                                                    value={accountField.value || undefined}
                                                    options={accountPickerOptions}
                                                    placeholder="Search account..."
                                                    emptyText="No matching accounts"
                                                    onChange={(value) => accountField.onChange(value)}
                                                />
                                            )}
                                        />
                                        <FieldErrorText message={errors.lines?.[index]?.account_id?.message} />
                                    </div>
                                    <div className="w-32 space-y-1">
                                        <div className="text-[11px] font-medium text-muted-foreground md:hidden">Debit (Left)</div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Debit (Left)"
                                            aria-label={`Debit amount for line ${index + 1}`}
                                            {...register(`lines.${index}.debit`, { valueAsNumber: true })}
                                            aria-invalid={!!errors.lines?.[index]?.debit}
                                            className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm text-right aria-[invalid=true]:border-red-500"
                                        />
                                    </div>
                                    <div className="w-32 space-y-1">
                                        <div className="text-[11px] font-medium text-muted-foreground md:hidden">Credit (Right)</div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Credit (Right)"
                                            aria-label={`Credit amount for line ${index + 1}`}
                                            {...register(`lines.${index}.credit`, { valueAsNumber: true })}
                                            aria-invalid={!!errors.lines?.[index]?.credit}
                                            className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm text-right aria-[invalid=true]:border-red-500"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={() => remove(index)}
                                        size="icon"
                                        variant="ghost"
                                        className="text-destructive hover:text-destructive"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t">
                        <div className="text-sm bg-muted px-4 py-2 rounded-md">
                            <span className={cn("font-medium pr-4", Math.abs(totalDebit - totalCredit) > 0.01 ? "text-destructive" : "text-green-700")}>
                                Balance: {(totalDebit - totalCredit).toFixed(2)}
                            </span>
                            <span className="text-muted-foreground">Total Dr: {totalDebit.toFixed(2)} | Total Cr: {totalCredit.toFixed(2)}</span>
                        </div>
                        {errors.lines && <span className="text-xs text-destructive">{errors.lines.message || errors.lines.root?.message}</span>}
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                        >
                            Post Journal
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
